import { useCallback, useState } from 'react';
import { config } from '../config/env';
import { useAuthContext } from '../contexts/AuthContext';

/**
 * Do an authenticated request to the Inspect-Action API.
 */
interface ApiFetchOptions {
  returnNonOkResponse?: boolean;
  /** Rethrow network failures instead of resolving to null. */
  throwErrors?: boolean;
}

export const useApiFetch = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const { ensureFreshSession, login } = useAuthContext();

  const apiFetch = useCallback(
    async (url: string, request?: RequestInit, options?: ApiFetchOptions) => {
      setIsLoading(true);
      setError(null);
      try {
        // Proactively refresh so the request rides a valid cookie session.
        await ensureFreshSession();

        url = url.startsWith('/') ? config.apiBaseUrl + url : url;

        const send = () =>
          fetch(url, {
            ...request,
            credentials: 'include',
          });

        let response = await send();

        // On a 401 the cookie was rejected: force a refresh (the freshness hint
        // can wrongly read fresh), retry once, else redirect to login.
        if (response.status === 401) {
          const refreshed = await ensureFreshSession(true);
          if (refreshed) {
            response = await send();
          }
          if (response.status === 401) {
            await login();
          }
        }

        if (!response.ok && !options?.returnNonOkResponse) {
          throw new Error(
            `API request failed: ${response.status} ${response.statusText}`
          );
        }
        return response;
      } catch (err) {
        if ((err as Error).name === 'AbortError') {
          return null;
        }
        setError(err as Error);
        if (options?.throwErrors) throw err;
        return null;
      } finally {
        setIsLoading(false);
      }
    },
    [ensureFreshSession, login]
  );

  return { apiFetch, isLoading, error };
};
